import type { PortfolioAccount } from '@/modules/projects/components/portfolio/types'
import type { Statement, Transaction } from '@/modules/projects/stores/types'
import {
  type AccountIndex,
  accountBadge,
  accountMonthStatus,
  indexAccount,
  isNotStarted,
} from './accountMonths'
import {
  buildAccountTimelines,
  findFailedReads,
  findVerifyingStatements,
} from './accountTimelines'
import { mergeActivity } from './activityEntries'
import {
  accountFilterOf,
  isInDates,
  placementDate,
  totalActivity,
} from './activityView'
import {
  dayAfter,
  monthLabel,
  monthOf,
  monthsBetween,
  nextMonth,
} from './months'
import { isFailedRead } from './statementReasons'
import { pairTransfers } from './transferPairing'
import type {
  AccountMonth,
  AccountMonthStatus,
  AccountStatement,
  AccountTimeline,
  ActivityEntry,
  ActivityTotals,
  Badge,
  BadgeReason,
  BadgeStatus,
  DateWindow,
  MonthKey,
  VerifyingStatement,
} from './types'

export interface PortfolioOverview {
  // Every month a statement ends in, oldest first. The As of picker lists these.
  months: MonthKey[]
  latestMonth: MonthKey | null
  // The first month with a total. Every change is measured from its end.
  baselineMonth: MonthKey | null
  timelines: AccountTimeline[]
  // Statements being verified that name no known account. They still block a total.
  unattributedVerifying: VerifyingStatement[]
  // The merged Activity list, transfers already paired. Feed it to activityViewFor.
  activity: ActivityEntry[]
  // Rows hidden because the other source carries the same event. The Activity list says how many.
  hiddenCopies: ActivityEntry[]
  series: SeriesPoint[]
}

// An account whose statements start after this month. It is not missing: it sits out the month.
export interface StartingAccount extends AccountMonth {
  last4: string
  // Null when the account has no statement at all.
  startMonth: MonthKey | null
}

export interface NotCounted extends AccountMonth {
  reason: 'no_statement' | 'verifying' | 'failed_read'
}

// A month whose statement gives no value. True when its securities read failed.
export interface UnknownValueMonth extends AccountMonth {
  failedRead: boolean
  // The read worked for the other accounts on the file but returned none of this one.
  readWithoutAccount: boolean
}

export const isReadWithoutAccount = (
  reasons: BadgeReason[],
  month: MonthKey,
): boolean =>
  reasons.some(
    (reason) => reason.code === 'account_not_read' && reason.month === month,
  )

// Where an account's cash figure came from. 'computed' = the printed balance less money market
// holdings. 'withheld' = no balance reaches this case at all, so the plan hides cash.
export type CashSource = 'printed' | 'computed' | 'not_captured' | 'withheld'

export interface AccountAsOf {
  accountId: string
  name: string
  institution: string
  last4: string
  accountType: AccountTimeline['accountType']
  month: MonthKey
  status: AccountMonthStatus
  // The statement that ends in this month. Null for every status but 'statement'.
  statement: AccountStatement | null
  verifyingStatement: VerifyingStatement | null
  securities: number | null
  cash: number | null
  // Null when no statement ends this month.
  cashSource: CashSource | null
  value: number | null
  // Null when there is no earlier statement or no value this month.
  change: { amount: number; since: string } | null
  // This account alone, so a transfer to another account in the case counts here.
  money: {
    window: DateWindow | null
    netMoney: number
    totals: ActivityTotals | null
  }
  statementCount: number
  firstStatementMonth: MonthKey | null
  lastStatementMonth: MonthKey | null
  // The month the account's statements start, even when that is after this month.
  statementsStartMonth: MonthKey | null
  badge: Badge
}

export interface RollupBadge {
  status: BadgeStatus
  reviewAccounts: string[]
  verifyingAccounts: string[]
  // "2 of 3 accounts need review: Fidelity •• 2231 and Charles Schwab •• 8817."
  sentence: string | null
}

export interface MonthTotal {
  month: MonthKey
  asOfDate: string
  // Null is "No total": some started account has no statement, is being verified, or is mid cycle.
  total: number | null
  securities: number | null
  cash: number | null
  // False when some counted statement printed no cash balance (the plan can hide it).
  cashIncluded: boolean
  // The accounts the month covers. Accounts whose statements have not started are not among them.
  accountCount: number
  // No statement for the month inside or after the account's run of statements.
  missing: AccountMonth[]
  verifying: AccountMonth[]
  midCycle: AccountMonth[]
  unknownValue: UnknownValueMonth[]
  twoStatements: AccountMonth[]
  notStarted: StartingAccount[]
}

export interface SeriesPoint extends MonthTotal {
  hasGap: boolean
  money: {
    window: DateWindow
    moneyIn: number
    moneyOut: number
    netMoney: number
  }
  notCounted: NotCounted[]
}

export interface AllAccountsAsOf extends MonthTotal {
  accounts: AccountAsOf[]
  // Measured from the baseline total, so it includes the value of any account that started since.
  change: {
    amount: number
    since: string
    startedSince: StartingAccount[]
  } | null
  badge: RollupBadge
  statementCount: number
  // From the first statement's start to the as-of date: "Sep 1 to Dec 31, 2022".
  period: DateWindow | null
  money: {
    window: DateWindow | null
    totals: ActivityTotals | null
    notCounted: NotCounted[]
  }
}

interface OverviewIndex {
  accounts: AccountIndex[]
  entriesByAccount: Map<string, ActivityEntry[]>
  asOfDateByMonth: Map<MonthKey, string>
  asOfCache: Map<MonthKey, AllAccountsAsOf>
  printedCash: Map<string, number | null>
  isCashSent: boolean
}

const indexes = new WeakMap<PortfolioOverview, OverviewIndex>()

const lastDayOfMonth = (month: MonthKey) => {
  const [year, monthNumber] = month.split('-').map(Number)
  const day = new Date(year, monthNumber, 0).getDate()
  return `${month}-${String(day).padStart(2, '0')}`
}

const accountMonth = (index: AccountIndex, month: MonthKey): AccountMonth => ({
  accountId: index.timeline.id,
  accountName: index.timeline.name,
  month,
})

const unattributedMonth = (
  statement: VerifyingStatement,
  month: MonthKey,
): AccountMonth => ({
  accountId: '',
  accountName: statement.statementFileName,
  month,
})

const valueAt = (
  index: AccountIndex,
  month: MonthKey,
  status: AccountMonthStatus,
) =>
  status === 'ended' ? 0 : (index.statementByMonth.get(month)?.value ?? null)

interface MonthTally {
  totalCents: number
  securitiesCents: number
  cashCents: number
  cashIncluded: boolean
  accountCount: number
}

const tallyAccount = (
  tally: MonthTally,
  index: AccountIndex,
  month: MonthKey,
  status: AccountMonthStatus,
) => {
  tally.accountCount += 1
  if (status === 'ended') return
  const statement = index.statementByMonth.get(month)
  tally.totalCents += Math.round((statement?.value ?? 0) * 100)
  tally.securitiesCents += Math.round((statement?.securities ?? 0) * 100)
  tally.cashCents += Math.round((statement?.cash ?? 0) * 100)
  if (statement?.cash == null) tally.cashIncluded = false
}

type MonthLists = Pick<
  MonthTotal,
  | 'missing'
  | 'verifying'
  | 'midCycle'
  | 'unknownValue'
  | 'twoStatements'
  | 'notStarted'
>

const startingAccount = (
  index: AccountIndex,
  month: MonthKey,
): StartingAccount => ({
  ...accountMonth(index, month),
  last4: index.timeline.last4,
  startMonth: index.firstMonth,
})

const unknownValueMonth = (
  index: AccountIndex,
  month: MonthKey,
  status: AccountMonthStatus,
): UnknownValueMonth => ({
  ...accountMonth(index, month),
  failedRead: status === 'failed_read',
  readWithoutAccount: isReadWithoutAccount(
    index.failedReadByMonth.get(month)?.reasons ?? [],
    month,
  ),
})

const placeAccount = (
  lists: MonthLists,
  tally: MonthTally,
  index: AccountIndex,
  month: MonthKey,
) => {
  const status = accountMonthStatus(index, month)
  const entry = accountMonth(index, month)
  if (isNotStarted(status)) lists.notStarted.push(startingAccount(index, month))
  else if (status === 'missing') lists.missing.push(entry)
  else if (status === 'verifying') lists.verifying.push(entry)
  else if (status === 'mid_cycle') lists.midCycle.push(entry)
  else if (status === 'two_statements') lists.twoStatements.push(entry)
  else if (valueAt(index, month, status) == null) {
    lists.unknownValue.push(unknownValueMonth(index, month, status))
  } else tallyAccount(tally, index, month, status)
}

const blockedCountOf = (lists: MonthLists) =>
  lists.missing.length +
  lists.verifying.length +
  lists.midCycle.length +
  lists.unknownValue.length +
  lists.twoStatements.length

const monthTotal = (
  accounts: AccountIndex[],
  unattributed: VerifyingStatement[],
  month: MonthKey,
  asOfDate: string,
): MonthTotal => {
  const tally: MonthTally = {
    totalCents: 0,
    securitiesCents: 0,
    cashCents: 0,
    cashIncluded: true,
    accountCount: 0,
  }
  const lists: MonthLists = {
    missing: [],
    verifying: unattributed
      .filter((statement) => statement.coveredMonths.includes(month))
      .map((statement) => unattributedMonth(statement, month)),
    midCycle: [],
    unknownValue: [],
    twoStatements: [],
    notStarted: [],
  }
  for (const index of accounts) placeAccount(lists, tally, index, month)
  const blockedCount = blockedCountOf(lists)
  const hasTotal = blockedCount === 0 && tally.accountCount > 0
  return {
    month,
    asOfDate,
    total: hasTotal ? tally.totalCents / 100 : null,
    securities: hasTotal ? tally.securitiesCents / 100 : null,
    cash: hasTotal ? tally.cashCents / 100 : null,
    cashIncluded: tally.cashIncluded,
    accountCount: tally.accountCount + blockedCount,
    ...lists,
  }
}

const baselineStatement = (
  index: AccountIndex,
  baselineMonth: MonthKey | null,
) =>
  (baselineMonth ? index.statementByMonth.get(baselineMonth) : undefined) ??
  index.timeline.statements[0] ??
  null

const accountChange = (
  index: AccountIndex,
  month: MonthKey,
  status: AccountMonthStatus,
  baselineMonth: MonthKey | null,
) => {
  const baseline = baselineStatement(index, baselineMonth)
  const value = valueAt(index, month, status)
  if (!baseline || baseline.value == null || value == null) return null
  if (month <= baseline.month) return null
  return {
    amount: Math.round((value - baseline.value) * 100) / 100,
    since: baseline.endDate,
  }
}

const inWindow = (entries: ActivityEntry[], window: DateWindow) =>
  entries.filter((entry) => isInDates(entry, window))

// Money starts the day after the value it is compared with, so the two periods line up.
const moneyWindow = (
  baseline: AccountStatement | null,
  month: MonthKey,
  asOfDate: string,
): DateWindow | null => {
  if (!baseline) return null
  const from =
    month > baseline.month
      ? dayAfter(baseline.endDate)
      : (baseline.startDate ?? `${baseline.month}-01`)
  return from > asOfDate ? null : { from, to: asOfDate }
}

const statementSpan = (statements: AccountStatement[]) => ({
  statementCount: statements.length,
  firstStatementMonth: statements[0]?.month ?? null,
  lastStatementMonth: statements[statements.length - 1]?.month ?? null,
})

const printedCashKey = (accountId: string, segmentId: number) =>
  `${accountId}:${segmentId}`

const printedCashOf = (accounts: PortfolioAccount[]) =>
  new Map(
    accounts.flatMap((account) =>
      account.periods.map(
        (period) =>
          [
            printedCashKey(account.id, period.segmentId),
            period.cashBalance,
          ] as const,
      ),
    ),
  )

// lukita withholds every balance without the reconcile feature. One balance anywhere in the case
// proves the plan has cash, so a statement with none was not captured.
const isCashSentFor = (accounts: PortfolioAccount[], statements: Statement[]) =>
  accounts.some((account) =>
    account.periods.some((period) => period.cashBalance != null),
  ) ||
  statements.some((statement) =>
    (statement.segments ?? []).some(
      (segment) =>
        segment.endBalance != null || segment.reconciliationDetail != null,
    ),
  )

const toCents = (amount: number) => Math.round(amount * 100)

const cashSourceOf = (
  overviewIndex: OverviewIndex,
  accountId: string,
  statement: AccountStatement,
): CashSource => {
  if (statement.cash == null) {
    return overviewIndex.isCashSent ? 'not_captured' : 'withheld'
  }
  const printed = overviewIndex.printedCash.get(
    printedCashKey(accountId, statement.segmentId),
  )
  return printed != null && toCents(printed) === toCents(statement.cash)
    ? 'printed'
    : 'computed'
}

const accountCashSource = (
  overviewIndex: OverviewIndex,
  accountId: string,
  statement: AccountStatement | null,
  status: AccountMonthStatus,
): CashSource | null => {
  if (status === 'ended') return 'computed'
  return statement ? cashSourceOf(overviewIndex, accountId, statement) : null
}

const cashNotCapturedReason = (statement: AccountStatement): BadgeReason => ({
  code: 'cash_not_captured',
  month: statement.month,
  sentence: `Cash balance not captured on the ${monthLabel(statement.month)} statement.`,
  action: {
    kind: 'open_statement',
    label: 'Open the statement',
    fileUrl: statement.statementFileUrl,
  },
})

// A statement that could not be read already says so; its missing cash is part of that.
const isCashNotCaptured = (
  overviewIndex: OverviewIndex,
  accountId: string,
  statement: AccountStatement,
) =>
  !isFailedRead(statement) &&
  cashSourceOf(overviewIndex, accountId, statement) === 'not_captured'

// Every statement up to the month counts, like the other statement reasons: an older one without
// cash makes the change since then short too.
const cashReasonsUpTo = (
  overviewIndex: OverviewIndex,
  index: AccountIndex,
  month: MonthKey,
): BadgeReason[] =>
  index.timeline.statements
    .filter(
      (statement) =>
        statement.month <= month &&
        isCashNotCaptured(overviewIndex, index.timeline.id, statement),
    )
    .reverse()
    .map(cashNotCapturedReason)

// The cash reason one statement adds to its account's badge, so the statements table agrees with it.
export const statementCashReasons = (
  overview: PortfolioOverview,
  accountId: string,
  statement: AccountStatement,
): BadgeReason[] => {
  const overviewIndex = indexes.get(overview)
  return overviewIndex && isCashNotCaptured(overviewIndex, accountId, statement)
    ? [cashNotCapturedReason(statement)]
    : []
}

const withCashReasons = (
  badge: Badge,
  cashReasons: BadgeReason[],
  month: MonthKey,
): Badge => {
  if (cashReasons.length === 0) return badge
  const reasons = [...badge.reasons, ...cashReasons]
  return {
    status: 'needs_review',
    reasons: [
      ...reasons.filter((reason) => reason.month === month),
      ...reasons.filter((reason) => reason.month !== month),
    ],
  }
}

const buildAccountAsOf = (
  index: AccountIndex,
  overview: PortfolioOverview,
  overviewIndex: OverviewIndex,
  month: MonthKey,
  asOfDate: string,
): AccountAsOf => {
  const { timeline } = index
  const entries = overviewIndex.entriesByAccount.get(timeline.id) ?? []
  const status = accountMonthStatus(index, month)
  const statement = index.statementByMonth.get(month) ?? null
  const window = moneyWindow(
    baselineStatement(index, overview.baselineMonth),
    month,
    asOfDate,
  )
  const totals = window
    ? totalActivity(inWindow(entries, window), accountFilterOf([timeline.id]))
    : null
  return {
    accountId: timeline.id,
    name: timeline.name,
    institution: timeline.institution,
    last4: timeline.last4,
    accountType: timeline.accountType,
    month,
    status,
    statement,
    verifyingStatement: index.verifyingByMonth.get(month) ?? null,
    securities: status === 'ended' ? 0 : (statement?.securities ?? null),
    cash: status === 'ended' ? 0 : (statement?.cash ?? null),
    cashSource: accountCashSource(
      overviewIndex,
      timeline.id,
      statement,
      status,
    ),
    value: valueAt(index, month, status),
    change: accountChange(index, month, status, overview.baselineMonth),
    money: { window, netMoney: totals?.netMoney ?? 0, totals },
    ...statementSpan(
      timeline.statements.filter((candidate) => candidate.month <= month),
    ),
    statementsStartMonth: index.firstMonth,
    badge: withCashReasons(
      accountBadge(index, month),
      cashReasonsUpTo(overviewIndex, index, month),
      month,
    ),
  }
}

const joinNames = (names: string[]) =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

export const rollupBadge = (
  accounts: Pick<AccountAsOf, 'name' | 'badge' | 'status'>[],
): RollupBadge => {
  const counted = accounts.filter(
    (account) => account.status !== 'before_first_statement',
  )
  const reviewAccounts = counted
    .filter((account) => account.badge.status === 'needs_review')
    .map((account) => account.name)
  const verifyingAccounts = counted
    .filter((account) => account.badge.status === 'verifying')
    .map((account) => account.name)
  if (reviewAccounts.length > 0) {
    const noun = counted.length === 1 ? 'account needs' : 'accounts need'
    return {
      status: 'needs_review',
      reviewAccounts,
      verifyingAccounts,
      sentence: `${reviewAccounts.length} of ${counted.length} ${noun} review: ${joinNames(reviewAccounts)}.`,
    }
  }
  if (verifyingAccounts.length > 0) {
    const verb = verifyingAccounts.length === 1 ? 'is' : 'are'
    return {
      status: 'verifying',
      reviewAccounts,
      verifyingAccounts,
      sentence: `${joinNames(verifyingAccounts)} ${verb} still being verified.`,
    }
  }
  return {
    status: 'verified',
    reviewAccounts,
    verifyingAccounts,
    sentence: null,
  }
}

// A statement still being verified whose account is not known yet. It blocks the total, so the
// rollup badge names it too instead of saying Verified.
const unattributedBadges = (total: MonthTotal) =>
  total.verifying
    .filter((entry) => entry.accountId === '')
    .map((entry) => ({
      name: entry.accountName,
      status: 'verifying' as const,
      badge: { status: 'verifying' as const, reasons: [] },
    }))

// The account-months inside a money window that have no rows to count.
const notCountedIn = (
  accounts: AccountIndex[],
  unattributed: VerifyingStatement[],
  months: MonthKey[],
): NotCounted[] =>
  months.flatMap((month) => [
    ...accounts.flatMap((index): NotCounted[] => {
      const status = accountMonthStatus(index, month)
      if (status === 'missing')
        return [{ ...accountMonth(index, month), reason: 'no_statement' }]
      if (status === 'verifying')
        return [{ ...accountMonth(index, month), reason: 'verifying' }]
      if (status === 'failed_read')
        return [{ ...accountMonth(index, month), reason: 'failed_read' }]
      return []
    }),
    ...unattributed
      .filter((statement) => statement.coveredMonths.includes(month))
      .map(
        (statement): NotCounted => ({
          ...unattributedMonth(statement, month),
          reason: 'verifying',
        }),
      ),
  ])

const baselineDateOf = (index: OverviewIndex, baselineMonth: MonthKey) =>
  index.asOfDateByMonth.get(baselineMonth) ?? lastDayOfMonth(baselineMonth)

const byStartMonth = (first: StartingAccount, second: StartingAccount) =>
  (first.startMonth ?? '').localeCompare(second.startMonth ?? '') ||
  first.accountName.localeCompare(second.accountName)

const startedBetween = (baseline: MonthTotal, total: MonthTotal) =>
  baseline.notStarted
    .filter(
      (account) =>
        !total.notStarted.some(
          (later) => later.accountId === account.accountId,
        ),
    )
    .sort(byStartMonth)

// The total now less the baseline total. An account that started in between adds its whole value.
const heroChange = (
  overview: PortfolioOverview,
  index: OverviewIndex,
  total: MonthTotal,
): AllAccountsAsOf['change'] => {
  const { baselineMonth } = overview
  if (total.total == null || !baselineMonth || total.month <= baselineMonth)
    return null
  const since = baselineDateOf(index, baselineMonth)
  const baseline = monthTotal(
    index.accounts,
    overview.unattributedVerifying,
    baselineMonth,
    since,
  )
  if (baseline.total == null) return null
  return {
    amount: (toCents(total.total) - toCents(baseline.total)) / 100,
    since,
    startedSince: startedBetween(baseline, total),
  }
}

const heroMoney = (
  overview: PortfolioOverview,
  index: OverviewIndex,
  month: MonthKey,
  asOfDate: string,
): AllAccountsAsOf['money'] => {
  const baselineDate = overview.baselineMonth
    ? index.asOfDateByMonth.get(overview.baselineMonth)
    : undefined
  const firstStart = earliestStart(overview.timelines)
  const from =
    baselineDate && overview.baselineMonth && month > overview.baselineMonth
      ? dayAfter(baselineDate)
      : firstStart
  if (!from) return { window: null, totals: null, notCounted: [] }
  const window = { from, to: asOfDate }
  const windowMonths = monthsBetween(monthOf(from), month)
  return {
    window,
    totals: totalActivity(
      inWindow(overview.activity, window),
      accountFilterOf(null),
    ),
    notCounted: notCountedIn(
      index.accounts,
      overview.unattributedVerifying,
      windowMonths,
    ),
  }
}

const earliestStart = (timelines: AccountTimeline[]): string | null => {
  const starts = timelines.flatMap((timeline) =>
    timeline.statements
      .slice(0, 1)
      .map((statement) => statement.startDate ?? `${statement.month}-01`),
  )
  return starts.length === 0
    ? null
    : starts.reduce((first, start) => (start < first ? start : first))
}

const buildAllAccountsAsOf = (
  overview: PortfolioOverview,
  index: OverviewIndex,
  month: MonthKey,
): AllAccountsAsOf => {
  const asOfDate = index.asOfDateByMonth.get(month) ?? lastDayOfMonth(month)
  const accounts = index.accounts.map((accountIndex) =>
    buildAccountAsOf(accountIndex, overview, index, month, asOfDate),
  )
  const total = monthTotal(
    index.accounts,
    overview.unattributedVerifying,
    month,
    asOfDate,
  )
  const firstStart = earliestStart(overview.timelines)
  return {
    ...total,
    accounts,
    change: heroChange(overview, index, total),
    badge: rollupBadge([...accounts, ...unattributedBadges(total)]),
    statementCount: accounts.reduce(
      (sum, account) => sum + account.statementCount,
      0,
    ),
    period: firstStart ? { from: firstStart, to: asOfDate } : null,
    money: heroMoney(overview, index, month, asOfDate),
  }
}

// A shallow copy of an overview (the hook adds ledger state) must answer accountsAsOf like the original.
export const shareOverviewIndex = (
  source: PortfolioOverview,
  copy: PortfolioOverview,
): void => {
  const index = indexes.get(source)
  if (index) indexes.set(copy, index)
}

// Every account and the all-accounts card as of one statement month. Cached per month.
export const accountsAsOf = (
  overview: PortfolioOverview,
  month: MonthKey,
): AllAccountsAsOf => {
  const index = indexes.get(overview)
  if (!index)
    throw new Error(
      'accountsAsOf needs an overview from buildPortfolioOverview',
    )
  const cached = index.asOfCache.get(month)
  if (cached) return cached
  const result = buildAllAccountsAsOf(overview, index, month)
  index.asOfCache.set(month, result)
  return result
}

const statementMonths = (
  timelines: AccountTimeline[],
  unattributed: VerifyingStatement[],
) =>
  Array.from(
    new Set([
      ...timelines.flatMap((timeline) => [
        ...timeline.statements.map((statement) => statement.month),
        ...timeline.verifying.map((statement) => statement.month),
      ]),
      ...unattributed.map((statement) => statement.month),
    ]),
  ).sort()

const asOfDates = (
  timelines: AccountTimeline[],
  unattributed: VerifyingStatement[],
) => {
  const dates = new Map<MonthKey, string>()
  const note = (month: MonthKey, endDate: string) => {
    const current = dates.get(month)
    if (!current || endDate > current) dates.set(month, endDate)
  }
  for (const timeline of timelines) {
    for (const statement of timeline.statements)
      note(statement.month, statement.endDate)
    for (const statement of timeline.verifying)
      note(statement.month, statement.endDate)
  }
  for (const statement of unattributed) note(statement.month, statement.endDate)
  return dates
}

const groupByAccount = (entries: ActivityEntry[]) => {
  const groups = new Map<string, ActivityEntry[]>()
  for (const entry of entries) {
    const group = groups.get(entry.accountId)
    if (group) group.push(entry)
    else groups.set(entry.accountId, [entry])
  }
  return groups
}

const MONEY_IN_FIELDS = ['deposits', 'transfersIn', 'unsortedIn'] as const
const MONEY_OUT_FIELDS = ['withdrawals', 'transfersOut', 'unsortedOut'] as const

// Each row falls in the window that ends at the first as-of date on or after it. A row with no
// printed date falls in the one holding its statement's end, so it is counted once.
const bucketByPoint = (entries: ActivityEntry[], pointDates: string[]) => {
  const buckets: ActivityEntry[][] = pointDates.map(() => [])
  for (const entry of entries) {
    const date = placementDate(entry)
    if (!date || date > pointDates[pointDates.length - 1]) continue
    let low = 0
    let high = pointDates.length - 1
    while (low < high) {
      const middle = Math.floor((low + high) / 2)
      if (pointDates[middle] >= date) high = middle
      else low = middle + 1
    }
    buckets[low].push(entry)
  }
  return buckets
}

const buildSeries = (
  overview: PortfolioOverview,
  index: OverviewIndex,
): SeriesPoint[] => {
  const pointDates = overview.months.map(
    (month) => index.asOfDateByMonth.get(month) ?? lastDayOfMonth(month),
  )
  const buckets = bucketByPoint(overview.activity, pointDates)
  const firstStart =
    earliestStart(overview.timelines) ?? `${overview.months[0]}-01`
  return overview.months.map((month, position) => {
    const total = monthTotal(
      index.accounts,
      overview.unattributedVerifying,
      month,
      pointDates[position],
    )
    const totals = totalActivity(buckets[position], accountFilterOf(null))
    const moneyIn =
      MONEY_IN_FIELDS.reduce(
        (sum, field) => sum + Math.round(totals[field].amount * 100),
        0,
      ) / 100
    const moneyOut =
      MONEY_OUT_FIELDS.reduce(
        (sum, field) => sum + Math.round(totals[field].amount * 100),
        0,
      ) / 100
    const from =
      position === 0 ? firstStart : dayAfter(pointDates[position - 1])
    const windowMonths =
      position === 0
        ? [month]
        : monthsBetween(nextMonth(overview.months[position - 1]), month)
    return {
      ...total,
      hasGap: total.missing.length > 0,
      money: {
        window: { from, to: pointDates[position] },
        moneyIn,
        moneyOut,
        netMoney: totals.netMoney,
      },
      notCounted: notCountedIn(
        index.accounts,
        overview.unattributedVerifying,
        windowMonths,
      ),
    }
  })
}

const firstMonthWithTotal = (
  overview: PortfolioOverview,
  index: OverviewIndex,
) =>
  overview.months.find(
    (month) =>
      monthTotal(
        index.accounts,
        overview.unattributedVerifying,
        month,
        lastDayOfMonth(month),
      ).total != null,
  ) ??
  overview.months[0] ??
  null

// cashInPlan comes from the account's plan. Without it, one balance in the case still proves cash.
export const buildPortfolioOverview = (
  accounts: PortfolioAccount[],
  statements: Statement[],
  transactions: Transaction[],
  cashInPlan = false,
): PortfolioOverview => {
  const verifying = findVerifyingStatements(statements)
  const timelines = buildAccountTimelines(
    accounts,
    verifying,
    findFailedReads(statements),
  )
  const knownLast4s = new Set(timelines.map((timeline) => timeline.last4))
  const unattributedVerifying = verifying.filter(
    (statement) => !statement.last4s.some((last4) => knownLast4s.has(last4)),
  )
  const merged = mergeActivity(accounts, timelines, transactions)
  const activity = pairTransfers(merged.entries)
  const months = statementMonths(timelines, unattributedVerifying)
  const overview: PortfolioOverview = {
    months,
    latestMonth: months[months.length - 1] ?? null,
    baselineMonth: null,
    timelines,
    unattributedVerifying,
    activity,
    hiddenCopies: merged.hiddenCopies,
    series: [],
  }
  const index: OverviewIndex = {
    accounts: timelines.map(indexAccount),
    entriesByAccount: groupByAccount(activity),
    asOfDateByMonth: asOfDates(timelines, unattributedVerifying),
    asOfCache: new Map(),
    printedCash: printedCashOf(accounts),
    isCashSent: cashInPlan || isCashSentFor(accounts, statements),
  }
  indexes.set(overview, index)
  overview.baselineMonth = firstMonthWithTotal(overview, index)
  overview.series = months.length === 0 ? [] : buildSeries(overview, index)
  return overview
}
